export type ConsentState = 'accepted' | 'declined' | null;

export interface ConsentRecord {
  decision: 'accepted' | 'declined';
  decidedAt: Date;
}

// Cookie value shape: "v1:accepted:<unix-seconds>". Bumping POLICY_VERSION
// invalidates every stored decision and forces a re-prompt.
const COOKIE_NAME = 'ellipsis_cookie_consent';
export const POLICY_VERSION = 'v1';
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

const KNOWN_REGISTRABLE_DOMAINS = ['ellipsis.sc', 'hireaboat.co.za'] as const;

const isBrowser = (): boolean =>
  typeof document !== 'undefined' && typeof window !== 'undefined';

const resolveCookieDomain = (): string | null => {
  if (!isBrowser()) return null;
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost') return null;
  if (/^[\d.]+$/.test(hostname) || hostname.includes(':')) return null;

  for (const registrable of KNOWN_REGISTRABLE_DOMAINS) {
    if (hostname === registrable || hostname.endsWith(`.${registrable}`)) {
      return `.${registrable}`;
    }
  }
  return null;
};

const parseCookie = (raw: string): ConsentRecord | null => {
  if (!raw) return null;
  const [version, value, ts] = raw.split(':');
  if (version !== POLICY_VERSION) return null;
  if (value !== 'accepted' && value !== 'declined') return null;
  if (!ts) return null;

  const seconds = Number(ts);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  return { decision: value, decidedAt: new Date(seconds * 1000) };
};

const readCookieRaw = (): string | null => {
  if (!isBrowser()) return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  const [, value] = match.split('=');
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const writeCookie = (
  decision: 'accepted' | 'declined',
  decidedAt: Date
): void => {
  if (!isBrowser()) return;
  const domain = resolveCookieDomain();
  const secure = window.location.protocol === 'https:';
  const seconds = Math.floor(decidedAt.getTime() / 1000);
  const parts = [
    `${COOKIE_NAME}=${POLICY_VERSION}:${decision}:${seconds}`,
    `Max-Age=${MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (secure) parts.push('Secure');
  document.cookie = parts.join('; ');
};

const clearCookie = (): void => {
  if (!isBrowser()) return;
  const domain = resolveCookieDomain();
  const parts = [`${COOKIE_NAME}=`, 'Max-Age=0', 'Path=/', 'SameSite=Lax'];
  if (domain) parts.push(`Domain=${domain}`);
  document.cookie = parts.join('; ');
};

export const readConsentRecord = (): ConsentRecord | null =>
  parseCookie(readCookieRaw() ?? '');

export const readConsent = (): ConsentState => {
  const record = readConsentRecord();
  return record?.decision ?? null;
};

export const writeConsent = (
  decision: 'accepted' | 'declined',
  decidedAt: Date = new Date()
): void => {
  writeCookie(decision, decidedAt);
};

export const clearConsent = (): void => {
  clearCookie();
};
